/**
 * Badge "ordini" e "forecast" da mostrare accanto a uno SKU, con tooltip al passaggio del
 * mouse.
 *
 * Il tooltip degli ordini e' lo stesso della pagina risultato ottimizzazione: il contenuto
 * vive qui in `OrderTooltipContent` cosi' le due pagine mostrano davvero la stessa cosa
 * invece di due copie che col tempo divergono.
 *
 * Il popup viene disegnato in un portale su document.body perche' le righe SKU stanno
 * dentro contenitori con overflow, che altrimenti lo taglierebbero.
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  filterShops,
  sameSelection,
  shopLabel,
  sumShopOrders,
  type LoadSkuOrdersByShop,
  type SkuOrdersShopEntry,
} from './ordersByShop';
import { ForecastProductsPanel, type LoadForecastProducts } from './ForecastProductsPanel';

export type TaglieBreakdown = Record<string | number, Record<string, number>>;

const TAGLIE_SORTED = [
  "XXXS", "XXS", "XS", "S", "M", "L", "XL", "XXL", "2XL", "XXXL", "3XL", "XXXXL", "4XL", "5XL", "6XL", "7XL", "8XL", "9XL", "10XL",
  "30", "30½", "31", "31½", "32", "32½", "33", "33½", "34", "34½", "35", "35½", "36", "36½", "37", "37½", "38", "38½", "39", "39½", "40", "40½", "41", "41½", "42", "42½", "43", "43½", "44", "44½", "45", "45½", "46", "46½", "47", "47½", "48", "48½", "49", "49½", "50", "50½", "51", "51½", "52", "52½",
];

const TAGLIE_INDEX = TAGLIE_SORTED.reduce<Record<string, number>>((acc, taglia, index) => {
  acc[taglia] = index;
  return acc;
}, {});

/** Ordinamento delle taglie: prima quelle note, poi i numeri, infine alfabetico. */
export const compareTaglie = (a: string, b: string) => {
  const aKey = a.trim();
  const bKey = b.trim();
  const aIndex = TAGLIE_INDEX[aKey];
  const bIndex = TAGLIE_INDEX[bKey];
  if (aIndex !== undefined || bIndex !== undefined) {
    return (aIndex ?? TAGLIE_SORTED.length) - (bIndex ?? TAGLIE_SORTED.length);
  }
  const aNum = Number(aKey.replace(",", "."));
  const bNum = Number(bKey.replace(",", "."));
  if (Number.isFinite(aNum) && Number.isFinite(bNum)) {
    return aNum - bNum;
  }
  return aKey.localeCompare(bKey, "en", { numeric: true });
};

export type OrderTooltipData = {
  orderQuantity: number;
  taglieOrder?: TaglieBreakdown;
  orderQuantityVenduta?: number;
  taglieOrderVenduta?: TaglieBreakdown;
  linkedOrderQuantity?: number;
  taglieLinkedOrder?: TaglieBreakdown;
};

const COLORE_ORDINATA = '#589A6B';
const COLORE_VENDUTA = '#9F6360';
const COLORE_LINKED = '#4F6FA8';
const COLORE_FORECAST = '#B99EA6';
const COLORE_FORECAST_BORDO = '#A5838C';

/** Posiziona il popup accanto al bottone, rientrando quando uscirebbe dallo schermo. */
export function computeTooltipPosition(rect: DOMRect, popupWidth = 320, maxPopupHeight = 350) {
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const padding = 12;
  const popupHeight = Math.min(maxPopupHeight, viewportHeight - padding * 2);

  let left = rect.right + 8;
  let top = rect.top;

  if (left + popupWidth > viewportWidth - padding) left = rect.left - popupWidth - 8;
  if (left < padding) left = Math.min(rect.left, viewportWidth - popupWidth - padding);
  if (top + popupHeight > viewportHeight - padding) top = viewportHeight - popupHeight - padding;
  if (top < padding) top = padding;

  return { x: left, y: top };
}

/** Contenitore del popup: cornice, ombra e posizione fissa. */
export function TooltipShell({ x, y, children }: { x: number; y: number; children: React.ReactNode }) {
  return (
    <div
      style={{
        position: 'fixed',
        left: x,
        top: y,
        zIndex: 999999,
        minWidth: '220px',
        maxWidth: '320px',
        maxHeight: '350px',
        overflowY: 'auto',
        borderRadius: '8px',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.05)',
        backgroundColor: 'white',
        padding: '16px',
        border: '2px solid #6b7280',
        pointerEvents: 'none',
      }}
    >
      {children}
    </div>
  );
}

/** Riga in fondo all'anteprima hover: il popup si puo' fissare per scegliere i negozi (SPJ-1364). */
export function PinOrdersHint() {
  return (
    <div style={{ marginTop: '10px', paddingTop: '8px', borderTop: '1px solid #e5e7eb', fontSize: '11px', color: '#6b7280' }}>
      Clicca per selezionare uno o più negozi
    </div>
  );
}

/** Corpo del tooltip ordini: quantita' e istogramma delle taglie. */
export function OrderTooltipContent({
  data,
  compareTaglie,
}: {
  data: OrderTooltipData;
  compareTaglie: (a: string, b: string) => number;
}) {
  const hasOrdinata =
    data.orderQuantity >= 1 && !!data.taglieOrder && Object.keys(data.taglieOrder).length > 0;
  const hasVenduta =
    (data.orderQuantityVenduta ?? 0) >= 1 &&
    !!data.taglieOrderVenduta &&
    Object.keys(data.taglieOrderVenduta).length > 0;
  const hasLinked =
    (data.linkedOrderQuantity ?? 0) >= 1 &&
    !!data.taglieLinkedOrder &&
    Object.keys(data.taglieLinkedOrder).length > 0;

  const ordinataData = hasOrdinata ? Object.values(data.taglieOrder!)[0] : {};
  const vendutaData = hasVenduta ? Object.values(data.taglieOrderVenduta!)[0] : {};
  const linkedData = hasLinked ? Object.values(data.taglieLinkedOrder!)[0] : {};

  const allSizes = new Set([
    ...Object.keys(ordinataData || {}),
    ...Object.keys(vendutaData || {}),
    ...Object.keys(linkedData || {}),
  ]);
  const sortedSizes = Array.from(allSizes).sort((a, b) => compareTaglie(String(a), String(b)));

  const combinedEntries = sortedSizes.map((size) => ({
    size,
    ordinata: Number((ordinataData as Record<string, number>)?.[size] || 0),
    venduta: Number((vendutaData as Record<string, number>)?.[size] || 0),
    linked: Number((linkedData as Record<string, number>)?.[size] || 0),
  }));
  const maxValue = Math.max(
    1,
    ...combinedEntries.map((e) => Math.max(e.ordinata, e.venduta, e.linked)),
  );

  return (
    <>
      <div style={{ marginBottom: '12px', paddingBottom: '8px', borderBottom: '1px solid #e5e7eb' }}>
        <span style={{ fontSize: '14px', fontWeight: 600, color: '#1f2937' }}>
          Ordini, Venduto e Run Collegata
        </span>
      </div>
      <div style={{ fontSize: '13px', color: '#374151', marginBottom: '12px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
          <span style={{ fontWeight: 500 }}>Quantità ordinata:</span>
          <span style={{ fontWeight: 600, color: COLORE_ORDINATA }}>{data.orderQuantity}</span>
        </div>
        {data.orderQuantityVenduta !== undefined && (
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ fontWeight: 500 }}>Quantità venduta:</span>
            <span style={{ fontWeight: 600, color: COLORE_VENDUTA }}>{data.orderQuantityVenduta}</span>
          </div>
        )}
        {data.linkedOrderQuantity !== undefined && data.linkedOrderQuantity > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
            <span style={{ fontWeight: 500 }}>Run collegata:</span>
            <span style={{ fontWeight: 600, color: COLORE_LINKED }}>{data.linkedOrderQuantity}</span>
          </div>
        )}
      </div>
      {sortedSizes.length > 0 && (
        <div style={{ fontSize: '12px' }}>
          <div style={{ fontWeight: 500, color: '#374151', marginBottom: '8px' }}>Distribuzione taglie:</div>
          <div style={{ display: 'flex', gap: '16px', marginBottom: '12px', fontSize: '10px' }}>
            {hasOrdinata && <Legenda colore={COLORE_ORDINATA} testo="Ordinata" />}
            {hasVenduta && <Legenda colore={COLORE_VENDUTA} testo="Venduta" />}
            {hasLinked && <Legenda colore={COLORE_LINKED} testo="Run collegata" />}
          </div>
          <div style={{ backgroundColor: '#f3f4f6', borderRadius: '4px', padding: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: '2px', height: '60px' }}>
              {combinedEntries.map(({ size, ordinata, venduta, linked }) => {
                const altezza = (v: number) => (v > 0 ? Math.max(4, (v / maxValue) * 50) : 0);
                const quante = [hasOrdinata, hasVenduta, hasLinked].filter(Boolean).length;
                const larghezza = quante > 1 ? '6px' : '14px';
                return (
                  <div
                    key={size}
                    style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, minWidth: '18px' }}
                  >
                    <div style={{ display: 'flex', gap: '1px', marginBottom: '2px', fontSize: '8px' }}>
                      {hasOrdinata && <span style={{ color: COLORE_ORDINATA }}>{ordinata}</span>}
                      {hasOrdinata && (hasVenduta || hasLinked) && <span style={{ color: '#9ca3af' }}>/</span>}
                      {hasVenduta && <span style={{ color: COLORE_VENDUTA }}>{venduta}</span>}
                      {hasVenduta && hasLinked && <span style={{ color: '#9ca3af' }}>/</span>}
                      {hasLinked && <span style={{ color: COLORE_LINKED }}>{linked}</span>}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: '1px', height: '50px' }}>
                      {hasOrdinata && <Barra altezza={altezza(ordinata)} larghezza={larghezza} colore={COLORE_ORDINATA} />}
                      {hasVenduta && <Barra altezza={altezza(venduta)} larghezza={larghezza} colore={COLORE_VENDUTA} />}
                      {hasLinked && <Barra altezza={altezza(linked)} larghezza={larghezza} colore={COLORE_LINKED} />}
                    </div>
                    <span style={{ fontSize: '8px', color: '#374151', marginTop: '2px', fontWeight: 500 }}>{size}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Legenda({ colore, testo }: { colore: string; testo: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
      <div style={{ width: '12px', height: '12px', backgroundColor: colore, borderRadius: '2px' }} />
      <span style={{ color: colore }}>{testo}</span>
    </div>
  );
}

function Barra({ altezza, larghezza, colore }: { altezza: number; larghezza: string; colore: string }) {
  return (
    <div style={{ height: `${altezza}px`, width: larghezza, backgroundColor: colore, borderRadius: '1px 1px 0 0' }} />
  );
}

/**
 * Popup ordini "fissato" (SPJ-1364): si apre al click sul badge ordini e resta aperto, cosi'
 * dentro si puo' usare un selettore cercabile e multi-selezione dei negozi. Con la selezione
 * iniziale (il negozio corrente) mostra esattamente il popup di sempre; cambiando selezione
 * mostra la stessa vista sommata sui negozi scelti.
 */
export const ORDERS_PANEL_WIDTH = 360;
export const ORDERS_PANEL_MAX_HEIGHT = 560;

// Una richiesta per SKU per sessione: il popup si riapre spesso sullo stesso SKU.
const shopsCache = new Map<string, Promise<SkuOrdersShopEntry[]>>();

function loadCached(sku: string, load: LoadSkuOrdersByShop): Promise<SkuOrdersShopEntry[]> {
  let pending = shopsCache.get(sku);
  if (!pending) {
    pending = load(sku).catch((err) => {
      shopsCache.delete(sku);
      throw err;
    });
    shopsCache.set(sku, pending);
  }
  return pending;
}

export function OrdersByShopPanel({
  anchorRect,
  sku,
  baseData,
  defaultShopIds,
  load,
  compareTaglie,
  onClose,
}: {
  anchorRect: DOMRect;
  sku: string;
  /** Il contenuto del popup di sempre, per il negozio corrente. */
  baseData: OrderTooltipData;
  /** Negozi del contesto corrente (vuoto nella vista area). */
  defaultShopIds: string[];
  load: LoadSkuOrdersByShop;
  compareTaglie: (a: string, b: string) => number;
  onClose: () => void;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  // Ancora il pannello al badge cliccato usando l'ALTEZZA REALE misurata (non quella massima
  // stimata), così non "salta" lontano dal trigger; si riposiziona quando il contenuto cambia
  // (caricamento negozi, apertura lista) e quando la finestra viene ridimensionata.
  const [pos, setPos] = useState(() =>
    computeTooltipPosition(anchorRect, ORDERS_PANEL_WIDTH, ORDERS_PANEL_MAX_HEIGHT),
  );
  useLayoutEffect(() => {
    const el = panelRef.current;
    if (!el) return;
    const reposition = () =>
      setPos(computeTooltipPosition(anchorRect, ORDERS_PANEL_WIDTH, el.getBoundingClientRect().height));
    reposition();
    const ro = new ResizeObserver(reposition);
    ro.observe(el);
    window.addEventListener('resize', reposition);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', reposition);
    };
  }, [anchorRect]);
  const defaultSet = useMemo(() => new Set(defaultShopIds), [defaultShopIds]);
  const [shops, setShops] = useState<SkuOrdersShopEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(() => new Set(defaultShopIds));
  const [query, setQuery] = useState('');
  const [listOpen, setListOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    loadCached(sku, load)
      .then((rows) => {
        if (!cancelled) setShops(rows);
      })
      .catch(() => {
        if (!cancelled) setError('Impossibile caricare gli ordini degli altri negozi.');
      });
    return () => {
      cancelled = true;
    };
  }, [sku, load]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    const onDown = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [onClose]);

  const isDefault = sameSelection(selected, defaultSet);
  const visibleShops = useMemo(() => filterShops(shops ?? [], query), [shops, query]);
  const allVisibleSelected = visibleShops.length > 0 && visibleShops.every((s) => selected.has(s.cod_negozio));
  const someVisibleSelected = visibleShops.some((s) => selected.has(s.cod_negozio));

  const data: OrderTooltipData | null = isDefault
    ? baseData
    : selected.size === 0 || !shops
      ? null
      : sumShopOrders(shops, selected);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllVisible = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) visibleShops.forEach((s) => next.delete(s.cod_negozio));
      else visibleShops.forEach((s) => next.add(s.cod_negozio));
      return next;
    });
  };

  const selectionLabel = (() => {
    if (isDefault && defaultSet.size > 1) return `Negozi della run (${defaultSet.size})`;
    if (isDefault && defaultSet.size === 1) return `Negozio corrente (${defaultShopIds[0]})`;
    if (isDefault) return 'Vista corrente';
    if (selected.size === 0) return 'Nessun negozio selezionato';
    if (shops && selected.size === shops.length) return `Tutti i negozi (${shops.length})`;
    if (selected.size === 1) {
      const only = shops?.find((s) => selected.has(s.cod_negozio));
      return only ? shopLabel(only) : [...selected][0];
    }
    return `${selected.size} negozi selezionati`;
  })();

  return (
    <div
      ref={panelRef}
      role="dialog"
      aria-label={`Ordini per negozio di ${sku}`}
      style={{
        position: 'fixed',
        left: pos.x,
        top: pos.y,
        zIndex: 999999,
        width: `${ORDERS_PANEL_WIDTH}px`,
        maxHeight: `min(${ORDERS_PANEL_MAX_HEIGHT}px, calc(100vh - 24px))`,
        overflowY: 'auto',
        borderRadius: '8px',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.05)',
        backgroundColor: 'white',
        padding: '16px',
        border: '2px solid #6b7280',
      }}
    >
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-wide text-gray-500">Negozi</div>
          <div className="text-xs font-semibold text-gray-800 truncate">{sku}</div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-gray-500 hover:text-gray-900 text-sm leading-none px-1"
          aria-label="Chiudi"
        >
          ✕
        </button>
      </div>

      <div className="relative mb-3">
        <button
          type="button"
          onClick={() => setListOpen((open) => !open)}
          disabled={!shops}
          className="w-full flex items-center justify-between gap-2 border border-gray-300 bg-white px-2 py-1.5 text-xs text-gray-800 hover:border-gray-500 disabled:text-gray-400"
          aria-expanded={listOpen}
        >
          <span className="truncate">{shops ? selectionLabel : error ? 'Negozi non disponibili' : 'Caricamento negozi…'}</span>
          <span aria-hidden>{listOpen ? '▴' : '▾'}</span>
        </button>

        {listOpen && shops && (
          <div className="mt-1 border border-gray-300 bg-white">
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Cerca codice o nome negozio"
              className="w-full border-b border-gray-200 px-2 py-1.5 text-xs outline-none"
            />
            <label className="flex items-center gap-2 px-2 py-1.5 text-xs font-semibold text-gray-800 border-b border-gray-200 cursor-pointer hover:bg-gray-50">
              <input
                type="checkbox"
                checked={allVisibleSelected}
                ref={(el) => {
                  if (el) el.indeterminate = !allVisibleSelected && someVisibleSelected;
                }}
                onChange={toggleAllVisible}
                disabled={visibleShops.length === 0}
              />
              {query.trim() ? `Seleziona tutti i risultati (${visibleShops.length})` : `Seleziona tutti (${shops.length})`}
            </label>
            <div style={{ maxHeight: '180px', overflowY: 'auto' }}>
              {visibleShops.length === 0 && (
                <div className="px-2 py-2 text-xs text-gray-500">Nessun negozio trovato</div>
              )}
              {visibleShops.map((shop) => (
                <label
                  key={shop.cod_negozio}
                  className="flex items-center gap-2 px-2 py-1 text-xs text-gray-800 cursor-pointer hover:bg-gray-50"
                >
                  <input
                    type="checkbox"
                    checked={selected.has(shop.cod_negozio)}
                    onChange={() => toggle(shop.cod_negozio)}
                  />
                  <span className="flex-1 truncate" title={shopLabel(shop)}>
                    {shopLabel(shop)}
                  </span>
                  <span className="tabular-nums text-gray-500" title="Ordinato / venduto">
                    {shop.quantity}/{shop.quantity_venduta}
                  </span>
                </label>
              ))}
            </div>
          </div>
        )}
        {error && <div className="mt-1 text-[11px] text-red-600">{error}</div>}
      </div>

      {!isDefault && selected.size > 0 && (
        <div className="mb-2 text-[11px] text-gray-500">
          Somma di {selected.size} {selected.size === 1 ? 'negozio' : 'negozi'}. La run collegata vale solo per i negozi della run.
        </div>
      )}
      {data ? (
        <OrderTooltipContent data={data} compareTaglie={compareTaglie} />
      ) : (
        <div className="text-xs text-gray-500">
          {selected.size === 0 ? 'Seleziona almeno un negozio.' : 'Caricamento…'}
        </div>
      )}
    </div>
  );
}

type PopupState = { x: number; y: number } | null;

/** Cosa serve al popup ordini fissato per caricare gli altri negozi (SPJ-1364). */
export type OrdersByShopConfig = {
  sku: string;
  defaultShopIds: string[];
  load: LoadSkuOrdersByShop;
};

/**
 * I due badge accanto allo SKU. `orders` e' opzionale: senza dati il badge non compare.
 * Con `ordersByShop`, un click sul badge ordini apre il popup fissato col selettore negozi;
 * il passaggio del mouse resta l'anteprima di sempre.
 *
 * Con `forecastProducts` (SPJ-1724) il badge forecast e' cliccabile e apre il popup con i
 * prodotti usati dal modello per il forecast; senza, mostra solo il numero.
 */
export function SkuOrderForecastBadges({
  orders,
  forecast,
  compareTaglie,
  ordersByShop,
  forecastProducts,
}: {
  orders?: OrderTooltipData;
  forecast?: number;
  compareTaglie: (a: string, b: string) => number;
  ordersByShop?: OrdersByShopConfig;
  forecastProducts?: { sku: string; shopIds: string[]; load: LoadForecastProducts };
}) {
  const [forecastPinned, setForecastPinned] = useState<DOMRect | null>(null);
  const [popup, setPopup] = useState<PopupState>(null);
  // Il pannello "pinned" tiene il rect del badge cliccato: il pannello si posiziona da solo
  // in base all'altezza reale (vedi OrdersByShopPanel), restando ancorato al trigger.
  const [pinned, setPinned] = useState<DOMRect | null>(null);
  const [container] = useState(() => (typeof document === 'undefined' ? null : document.createElement('div')));

  useEffect(() => {
    if (!container || typeof document === 'undefined') return;
    document.body.appendChild(container);
    return () => {
      if (container.parentNode === document.body) document.body.removeChild(container);
    };
  }, [container]);

  const mostraOrdini = !!orders;
  const mostraForecast = forecast !== undefined && forecast !== null;
  if (!mostraOrdini && !mostraForecast) return null;

  const apriPopupOrdini = (e: React.MouseEvent<HTMLElement>) => {
    if (pinned) return;
    setPopup(computeTooltipPosition(e.currentTarget.getBoundingClientRect()));
  };

  const fissaPopupOrdini = (e: React.MouseEvent<HTMLElement>) => {
    if (!ordersByShop) return;
    e.stopPropagation();
    setPopup(null);
    setPinned(e.currentTarget.getBoundingClientRect());
  };

  // Forma condivisa dai due badge; i colori li mette ciascuno per conto suo.
  const formaBadge = 'px-2 py-1 text-xs font-bold border transition-colors flex items-center gap-1';
  const stileBadge = `${formaBadge} border-gray-400 bg-gray-100 text-gray-700 hover:bg-gray-200`;

  return (
    <>
      {mostraOrdini && (
        <span
          className={`${stileBadge}${ordersByShop ? ' cursor-pointer' : ''}`}
          title={ordersByShop ? 'Ordini già piazzati — clic per vedere gli altri negozi' : 'Ordini già piazzati'}
          onMouseEnter={apriPopupOrdini}
          onMouseLeave={() => setPopup(null)}
          onClick={fissaPopupOrdini}
          role={ordersByShop ? 'button' : undefined}
        >
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {orders!.orderQuantity}
          {orders!.linkedOrderQuantity ? ` +${orders!.linkedOrderQuantity}` : ''}
        </span>
      )}
      {mostraForecast && (
        <span
          className={formaBadge}
          // Testo scuro e non bianco: su questo rosa il bianco starebbe attorno a 2,5:1
          // di contrasto, sotto la soglia leggibile; cosi' si sta oltre 5:1.
          style={{ backgroundColor: COLORE_FORECAST, borderColor: COLORE_FORECAST_BORDO, color: '#1f2937', ...(forecastProducts ? { cursor: 'pointer' } : {}) }}
          title={forecastProducts
            ? `Forecast: ${forecast} pezzi previsti — clic per vedere i prodotti usati dal modello`
            : `Forecast: ${forecast} pezzi previsti`}
          role={forecastProducts ? 'button' : undefined}
          tabIndex={forecastProducts ? 0 : undefined}
          onClick={forecastProducts ? (e) => {
            e.stopPropagation();
            setPopup(null);
            setForecastPinned(e.currentTarget.getBoundingClientRect());
          } : undefined}
          onKeyDown={forecastProducts ? (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setForecastPinned(e.currentTarget.getBoundingClientRect());
            }
          } : undefined}
        >
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 17l6-6 4 4 8-8m0 0v5m0-5h-5" />
          </svg>
          {forecast}
        </span>
      )}
      {forecastPinned &&
        forecastProducts &&
        container &&
        createPortal(
          <ForecastProductsPanel
            anchorRect={forecastPinned}
            sku={forecastProducts.sku}
            shopIds={forecastProducts.shopIds}
            forecast={forecast}
            load={forecastProducts.load}
            onClose={() => setForecastPinned(null)}
          />,
          container,
        )}
      {pinned &&
        orders &&
        ordersByShop &&
        container &&
        createPortal(
          <OrdersByShopPanel
            anchorRect={pinned}
            sku={ordersByShop.sku}
            baseData={orders}
            defaultShopIds={ordersByShop.defaultShopIds}
            load={ordersByShop.load}
            compareTaglie={compareTaglie}
            onClose={() => setPinned(null)}
          />,
          container,
        )}
      {popup &&
        !pinned &&
        orders &&
        container &&
        createPortal(
          <TooltipShell x={popup.x} y={popup.y}>
            <OrderTooltipContent data={orders} compareTaglie={compareTaglie} />
            {ordersByShop && <PinOrdersHint />}
          </TooltipShell>,
          container,
        )}
    </>
  );
}
